import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { GcsConnection } from '../services/gcs/GcsConnection';
import { CenterOpenState } from '../types/gcs/center';
import type { CenterOpenStateNotification, SetCenterOpenStateRequest } from '../types/gcs/center';
import type {
  AircraftId,
  ForeignAircraftDetailsNotification,
  ForeignAircraftRiskVolumeNotification
} from '../types/gcs/foreignAircraft';
import type { GcsConfig } from '../types/gcs/gcsConfig';
import { GcsData } from '../types/gcs/gcsData';
import type { LaunchpadAssignmentsNotification } from '../types/gcs/launchpadAssignment';
import type {
  MissionCreatedNotification,
  MissionDetailedStateNotification,
  MissionEtasNotification,
  MissionFailureModeRouteInfo,
  MissionFinishedNotification,
  MissionFlightDataNotification,
  MissionId,
  MissionLockingInfoNotification,
  MissionLogMessageNotification,
  MissionPreflightStateNotification,
  MissionRouteNotification,
  MissionRpicInControlNotification,
  MissionTimestampsNotification
} from '../types/gcs/mission';
import type { PreflightMissionAtLaunchpadNotification, PreflightOverviewNotification } from '../types/gcs/preflight';
import type { UssConnectionStateNotification, UssLogMessageNotification } from '../types/gcs/uss';
import type {
  Vehicle,
  VehicleId,
  VehicleModuleVersion,
  VehicleMissionComputerStatusNotification,
  VehiclesPreparationStatusNotification,
  VehicleStateNotification
} from '../types/gcs/vehicle';
import type { Version } from '../types/gcs/version';
import { AsyncAction } from '../types/general/asyncAction';
import { Connected, ConnectionState } from '../types/general/connectionState';
import { createLogger, type Logger } from '../utils/logger';
import { AppError, ErrorCode, ErrorDomain } from '../types/errors/errors';
import type { PostflightMissionSummary } from '../types/gcs/postflight';

class GcsConnectionManager {
  private static instance: GcsConnectionManager;
  private connections: Map<string, GcsConnection> = new Map();

  static getInstance(): GcsConnectionManager {
    if (!GcsConnectionManager.instance) {
      GcsConnectionManager.instance = new GcsConnectionManager();
    }
    return GcsConnectionManager.instance;
  }

  addConnection(gcsId: string, gcsConnection: GcsConnection): void {
    this.connections.set(gcsId, gcsConnection);
  }

  removeConnection(gcsId: string): void {
    this.connections.delete(gcsId);
  }

  getConnection(gcsId: string): GcsConnection | undefined {
    return this.connections.get(gcsId);
  }
}

export interface GcsStore {
  data: Map<string, GcsData>;

  // Connection management
  addGcsConnection: (config: GcsConfig) => void;
  removeGcsConnection: (gcsId: string) => void;
  connect: (gcsId: string, queryParams?: Record<string, string>) => void;
  connectAll: () => void;
  disconnect: (gcsId: string) => void;
  disconnectAll: () => void;

  // Authentication
  isLoggedIn: (gcsId: string) => Promise<boolean>;
  signInWithOkta: (gcsId: string, code: string, redirectUri: string | undefined) => Promise<void>;
  signOut: (gcsId: string) => Promise<void>;

  // Mission/Vehicle actions
  approveTakeoff: (gcsId: string, missionId: MissionId) => Promise<void>;
  cancelPreflight: (gcsId: string, missionId: MissionId, requeuePackage: boolean) => Promise<void>;
  safeUp: (gcsId: string, missionId: MissionId) => Promise<void>;
  rtl: (gcsId: string, missionId: MissionId) => Promise<void>;
  emergencyLand: (gcsId: string, missionId: MissionId) => Promise<void>;
  triggerParachute: (gcsId: string, missionId: MissionId) => Promise<void>;
  triggerFcLoss: (gcsId: string, missionId: MissionId) => Promise<void>;
  triggerDaa: (gcsId: string, missionId: MissionId) => Promise<void>;
  triggerMotorFailure: (gcsId: string, vehicleId: VehicleId) => Promise<void>;
  triggerVeryLowBattery: (gcsId: string, vehicleId: VehicleId) => Promise<void>;
  triggerVibrationExceedance: (gcsId: string, missionId: MissionId) => Promise<void>;
  triggerVeering: (gcsId: string, missionId: MissionId) => Promise<void>;
  triggerGpsDegradation: (gcsId: string, vehicleId: VehicleId) => Promise<void>;
  submitDiscrepancyReport: (gcsId: string, vehicleId: VehicleId, description: string) => Promise<void>;
  fetchMissionNote: (gcsId: string, missionId: MissionId) => Promise<string | null>;
  setMissionNote: (gcsId: string, missionId: MissionId, note: string) => Promise<void>;
  setCenterOpenState: (gcsId: string, newState: CenterOpenState) => Promise<void>;
  fetchPostflightMissionSummary: (gcsId: string, missionId: MissionId) => Promise<PostflightMissionSummary>;
  fetchVersion: (gcsId: string) => Promise<Version | null>;
  fetchVehicleAllModuleVersions: (gcsId: string, vehicleId: VehicleId) => Promise<readonly VehicleModuleVersion[]>;
  restartVehicle: (gcsId: string, vehicleId: VehicleId) => Promise<void>;
  disconnectVehicle: (gcsId: string, vehicleId: VehicleId) => Promise<void>;
  triggerRemoteIdFailure: (gcsId: string, vehicleId: VehicleId) => Promise<void>;
}

export const useGcsStore = create<GcsStore>()(
  subscribeWithSelector((set, get) => {
    const gcsConnectionManager = GcsConnectionManager.getInstance();
    const gcsConnectionStateUnlistenersMap = new Map<string, () => void>();
    const loggersMap = new Map<string, Logger>();

    // Maps to track foreign aircraft expiration timeouts
    const foreignAircraftDetailsTimeouts = new Map<string, Map<AircraftId, number>>();
    const foreignAircraftRiskVolumeTimeouts = new Map<string, Map<AircraftId, number>>();
    const lastFetchGcsStaticDataTimestampMap = new Map<string, number>();
    const GCS_STATIC_DATA_REFRESH_MIN_INTERVAL_MS = 2000;

    const updateGcsData = (gcsId: string, updater: (data: GcsData) => GcsData) => {
      set((prev) => {
        const newData = new Map(prev.data);
        const currentData = newData.get(gcsId) ?? GcsData.initial();
        newData.set(gcsId, updater(currentData));
        return { data: newData };
      });
    };

    const getLogger = (gcsId: string) => {
      let logger = loggersMap.get(gcsId);
      if (!logger) {
        logger = createLogger(gcsId);
        loggersMap.set(gcsId, logger);
      }
      return logger;
    };

    const getRequiredGcsConnection = (gcsId: string): GcsConnection => {
      const gcsConnection = gcsConnectionManager.getConnection(gcsId);
      if (!gcsConnection) {
        throw new AppError({
          domain: ErrorDomain.BUSINESS,
          code: ErrorCode.GCS_NOT_FOUND,
          params: { gcsId }
        });
      }

      return gcsConnection;
    };

    const fetchGcsStaticData = (gcsId: string, gcsConnection: GcsConnection): void => {
      const gcsData = get().data.get(gcsId);
      const hasStaticDataFailure =
        gcsData?.user.isFailure ||
        gcsData?.vehicles.isFailure ||
        gcsData?.launchpads.isFailure ||
        gcsData?.flightNetwork.isFailure ||
        gcsData?.center.isFailure;

      const now = Date.now();
      const lastAttemptAt = lastFetchGcsStaticDataTimestampMap.get(gcsId) ?? 0;
      if (!hasStaticDataFailure && now - lastAttemptAt < GCS_STATIC_DATA_REFRESH_MIN_INTERVAL_MS) {
        getLogger(gcsId).info(`Skipping fetch GCS static data`);
        return;
      }

      lastFetchGcsStaticDataTimestampMap.set(gcsId, now);
      fetchCurrentUser(gcsId, gcsConnection);
      fetchVehicles(gcsId, gcsConnection);
      fetchLaunchpads(gcsId, gcsConnection);
      fetchFlightNetwork(gcsId, gcsConnection);
      fetchCenter(gcsId, gcsConnection);
    };

    // Notification handlers
    const createHandlers = (gcsId: string, gcsConnection: GcsConnection) => ({
      handleConnectionStateChanged: (state: ConnectionState) => {
        getLogger(gcsId).info('Connection state: ', state);
        const prevState = get().data.get(gcsId)?.connectionState;

        updateGcsData(gcsId, (data) => data.withConnectionState(state));

        // Clean up foreign aircraft timeouts on disconnect
        if (!(state instanceof Connected)) {
          // Clear all foreign aircraft details timeouts for this GCS
          const detailsTimeouts = foreignAircraftDetailsTimeouts.get(gcsId);
          if (detailsTimeouts) {
            detailsTimeouts.forEach((timeout) => clearTimeout(timeout));
            detailsTimeouts.clear();
          }

          // Clear all foreign aircraft risk volume timeouts for this GCS
          const riskVolumeTimeouts = foreignAircraftRiskVolumeTimeouts.get(gcsId);
          if (riskVolumeTimeouts) {
            riskVolumeTimeouts.forEach((timeout) => clearTimeout(timeout));
            riskVolumeTimeouts.clear();
          }
        }

        // Fetch data and subscribe to topics on connect/reconnect
        if (state instanceof Connected && !(prevState instanceof Connected)) {
          onConnected(gcsId, gcsConnection);
        }
      },

      handleCenterOpenStateNotification: (notification: CenterOpenStateNotification) => {
        const prevCenterOpenState = get().data.get(gcsId)?.centerOpenState?.openState;

        updateGcsData(gcsId, (data) => data.withCenterOpenState(notification));

        if (
          notification.openState === CenterOpenState.CENTER_OPEN &&
          prevCenterOpenState !== CenterOpenState.CENTER_OPEN
        ) {
          onCenterOpened(gcsId, gcsConnection);
        }
      },

      handleMissionCreatedNotification: (notification: MissionCreatedNotification) => {
        updateGcsData(gcsId, (data) => data.addLiveMission(notification));
        gcsConnection.subscribeToMissionState(notification.missionId).catch((err) => {
          getLogger(gcsId).error(`Failed to subscribe to mission state for ${notification.missionId}:`, err);
        });
      },

      handleMissionFinishedNotification: (notification: MissionFinishedNotification) => {
        updateGcsData(gcsId, (data) => data.removeMissionData(notification.missionId));
        gcsConnection.unsubscribeFromMissionState(notification.missionId).catch((err) => {
          getLogger(gcsId).error(`Failed to unsubscribe from mission state for ${notification.missionId}:`, err);
        });
      },

      handleLaunchpadAssignmentNotification: (notification: LaunchpadAssignmentsNotification) => {
        const newMap = new Map();
        if (notification.assignments) {
          notification.assignments.forEach((assignment) => {
            newMap.set(assignment.vehicleId, assignment.launchpadId);
          });
        }
        updateGcsData(gcsId, (data) => data.withVehicleLaunchpadMap(newMap));
      },

      handleVehicleStateNotification: (notification: VehicleStateNotification) => {
        updateGcsData(gcsId, (data) => data.updateVehicleState(notification));
      },

      handleVehiclePreparationStatusNotification: (notification: VehiclesPreparationStatusNotification) => {
        const vehiclePreparationStatusMap = new Map(
          Object.entries(notification.statusByVehicleId).map(([vehicleId, status]) => [Number(vehicleId), status])
        );
        updateGcsData(gcsId, (data) => data.withVehiclePreparationStatusMap(vehiclePreparationStatusMap));
      },

      handleForeignAircraftDetailsNotification: (notification: ForeignAircraftDetailsNotification) => {
        const validUntilTime = new Date(notification.validUntilTimestamp).getTime();
        if (validUntilTime <= Date.now()) {
          getLogger(gcsId).warn(
            'Ignoring expired ForeignAircraftDetailsNotification',
            notification.aircraftId,
            notification.validUntilTimestamp
          );
          return;
        }

        updateGcsData(gcsId, (data) => data.updateForeignAircraftDetails(notification));

        // Set up expiration timeout based on validUntilTimestamp
        const aircraftId = notification.aircraftId;
        const currentTime = Date.now();

        const timeoutDuration = Math.max(0, new Date(notification.validUntilTimestamp).getTime() - currentTime);

        // Clear any existing timeout for this aircraft
        let gcsTimeouts = foreignAircraftDetailsTimeouts.get(gcsId);
        if (!gcsTimeouts) {
          gcsTimeouts = new Map();
          foreignAircraftDetailsTimeouts.set(gcsId, gcsTimeouts);
        }

        const existingTimeout = gcsTimeouts.get(aircraftId);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }

        // Set new timeout
        const timeout = setTimeout(() => {
          getLogger(gcsId).info('ForeignAircraftDetailsNotification expired, removing', aircraftId);
          updateGcsData(gcsId, (data) => data.removeForeignAircraftDetails(aircraftId));
          gcsTimeouts?.delete(aircraftId);
        }, timeoutDuration);

        gcsTimeouts.set(aircraftId, timeout);
      },

      handleForeignAircraftRiskVolumeNotification: (notification: ForeignAircraftRiskVolumeNotification) => {
        const validUntilTime = new Date(notification.validUntilTimestamp).getTime();
        if (validUntilTime <= Date.now()) {
          getLogger(gcsId).warn(
            'Ignoring expired ForeignAircraftRiskVolumeNotification',
            notification.aircraftId,
            notification.validUntilTimestamp
          );
          return;
        }

        updateGcsData(gcsId, (data) => data.updateForeignAircraftRiskVolume(notification));

        // Set up expiration timeout based on validUntilTimestamp
        const aircraftId = notification.aircraftId;
        const currentTime = Date.now();
        const timeoutDuration = Math.max(0, new Date(notification.validUntilTimestamp).getTime() - currentTime);

        // Clear any existing timeout for this aircraft
        let gcsTimeouts = foreignAircraftRiskVolumeTimeouts.get(gcsId);
        if (!gcsTimeouts) {
          gcsTimeouts = new Map();
          foreignAircraftRiskVolumeTimeouts.set(gcsId, gcsTimeouts);
        }

        const existingTimeout = gcsTimeouts.get(aircraftId);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }

        // Set new timeout
        const timeout = setTimeout(() => {
          getLogger(gcsId).info('ForeignAircraftRiskVolumeNotification expired, removing', aircraftId);
          updateGcsData(gcsId, (data) => data.removeForeignAircraftRiskVolume(aircraftId));
          gcsTimeouts?.delete(aircraftId);
        }, timeoutDuration);

        gcsTimeouts.set(aircraftId, timeout);
      },

      handlePreflightOverviewNotification: (notification: PreflightOverviewNotification) => {
        updateGcsData(gcsId, (data) => data.updateMissionPreflightOverview(notification));
      },

      handlePreflightMissionAtLaunchpadNotification: (notification: PreflightMissionAtLaunchpadNotification) => {
        updateGcsData(gcsId, (data) => data.updateLaunchpadPreflightMission(notification));
      },

      handleVehicleMissionComputerStatusNotification: (notification: VehicleMissionComputerStatusNotification) => {
        updateGcsData(gcsId, (data) => data.updateVehicleMissionComputerStatus(notification));
      },

      handleMissionDetailedStateNotification: (notification: MissionDetailedStateNotification) => {
        updateGcsData(gcsId, (data) => data.updateMissionDetailedState(notification));
      },

      handleMissionRouteNotification: (notification: MissionRouteNotification) => {
        updateGcsData(gcsId, (data) => data.updateMissionRoute(notification));
      },

      handleMissionFailureModeRouteInfoNotification: (notification: MissionFailureModeRouteInfo) => {
        updateGcsData(gcsId, (data) => data.updateMissionFailureModeRouteInfo(notification));
      },

      handleMissionLockingInfoNotification: (notification: MissionLockingInfoNotification) => {
        updateGcsData(gcsId, (data) => data.updateMissionLockingInfo(notification));
      },

      handleMissionFlightDataNotification: (notification: MissionFlightDataNotification) => {
        updateGcsData(gcsId, (data) => data.updateMissionFlightData(notification));
      },

      handleMissionPreflightStateNotification: (notification: MissionPreflightStateNotification) => {
        updateGcsData(gcsId, (data) => data.updateMissionPreflightState(notification));
      },

      handleMissionEtasNotification: (notification: MissionEtasNotification) => {
        updateGcsData(gcsId, (data) => data.updateMissionEtas(notification));
      },

      handleMissionTimestampsNotification: (notification: MissionTimestampsNotification) => {
        updateGcsData(gcsId, (data) => data.updateMissionTimestamps(notification));
      },

      handleMissionRpicInControlNotification: (notification: MissionRpicInControlNotification) => {
        updateGcsData(gcsId, (data) => data.updateMissionRpicInControl(notification));
      },

      handleMissionLogMessageNotification: (notification: MissionLogMessageNotification) => {
        updateGcsData(gcsId, (data) => data.addMissionLogMessage(notification));
      },

      handleUssConnectionStateNotification: (notification: UssConnectionStateNotification) => {
        updateGcsData(gcsId, (data) => data.withUssConnectionState(notification.state));
      },

      handleUssLogMessageNotification: (notification: UssLogMessageNotification) => {
        updateGcsData(gcsId, (data) => data.addUssLogMessage(notification));
      }
    });

    const onConnected = (gcsId: string, gcsConnection: GcsConnection) => {
      getLogger(gcsId).info('Connected, initializing subscriptions');
      fetchGcsStaticData(gcsId, gcsConnection);

      const handlers = createHandlers(gcsId, gcsConnection);

      // Subscribe to center open state
      gcsConnection.listenToCenterOpenStateChanged(handlers.handleCenterOpenStateNotification);
      gcsConnection.subscribeToCenterOpenState().catch((err) => {
        getLogger(gcsId).error(`Failed to subscribe to center open state:`, err);
      });

      // Clear all cached mission data first to avoid stale missions that may never receive updates.
      updateGcsData(gcsId, (data) => data.clearAllMissionData());

      // Subscribe to live missions
      gcsConnection.listenToMissionCreatedNotification(handlers.handleMissionCreatedNotification);
      gcsConnection.listenToMissionFinishedNotification(handlers.handleMissionFinishedNotification);
      gcsConnection.subscribeToLiveMissions().catch((err) => {
        getLogger(gcsId).error(`Failed to subscribe to live missions:`, err);
      });

      // Subscribe to launchpad assignments
      gcsConnection.listenToLaunchpadAssignmentChanged(handlers.handleLaunchpadAssignmentNotification);
      gcsConnection.subscribeToLaunchpadAssignment().catch((err) => {
        getLogger(gcsId).error(`Failed to subscribe to launchpad assignment:`, err);
      });

      // Subscribe to vehicle state
      gcsConnection.listenToVehicleStateChanged(handlers.handleVehicleStateNotification);
      gcsConnection.subscribeToVehicleState().catch((err) => {
        getLogger(gcsId).error(`Failed to subscribe to vehicle state:`, err);
      });

      // Subscribe to vehicle preparation status
      gcsConnection.listenToVehiclesPreparationStatusChanged(handlers.handleVehiclePreparationStatusNotification);
      gcsConnection.subscribeToVehiclePreparationStatus().catch((err) => {
        getLogger(gcsId).error(`Failed to subscribe to vehicle preparation status:`, err);
      });

      // Subscribe to foreign aircraft
      gcsConnection.listenToForeignAircraftDetailsChanged(handlers.handleForeignAircraftDetailsNotification);
      gcsConnection.listenToForeignAircraftRiskVolumeChanged(handlers.handleForeignAircraftRiskVolumeNotification);
      gcsConnection.subscribeToForeignAircraft().catch((err) => {
        getLogger(gcsId).error(`Failed to subscribe to foreign aircraft:`, err);
      });

      // Subscribe to USS
      gcsConnection.listenToUssConnectionState(handlers.handleUssConnectionStateNotification);
      gcsConnection.listenToUssLogMessage(handlers.handleUssLogMessageNotification);
      gcsConnection.subscribeToUss().catch((err) => {
        getLogger(gcsId).error(`Failed to subscribe to USS:`, err);
      });

      // Subscribe to preflight mission overview
      gcsConnection.listenToPreflightMissionOverviewChanged(handlers.handlePreflightOverviewNotification);
      gcsConnection.subscribeToPreflightMissionOverview().catch((err) => {
        getLogger(gcsId).error(`Failed to subscribe to preflight mission overview:`, err);
      });

      // Subscribe to preflight mission at launchpad
      gcsConnection.listenToPreflightMissionAtLaunchpadChanged(handlers.handlePreflightMissionAtLaunchpadNotification);
      gcsConnection.subscribeToPreflightMissionAtLaunchpad().catch((err) => {
        getLogger(gcsId).error(`Failed to subscribe to preflight mission at launchpad:`, err);
      });

      // Subscribe to vehicle mission computer status
      gcsConnection.listenToVehicleMissionComputerStatusChanged(
        handlers.handleVehicleMissionComputerStatusNotification
      );
      // Subscription is on per vehicle basis

      // Add listeners to mission state (for all missions)
      gcsConnection.listenToMissionDetailedState(handlers.handleMissionDetailedStateNotification);
      gcsConnection.listenToMissionRoute(handlers.handleMissionRouteNotification);
      gcsConnection.listenToMissionFailureModeRouteInfo(handlers.handleMissionFailureModeRouteInfoNotification);
      gcsConnection.listenToMissionLockingInfo(handlers.handleMissionLockingInfoNotification);
      gcsConnection.listenToMissionFlightData(handlers.handleMissionFlightDataNotification);
      gcsConnection.listenToMissionPreflightState(handlers.handleMissionPreflightStateNotification);
      gcsConnection.listenToMissionEtas(handlers.handleMissionEtasNotification);
      gcsConnection.listenToMissionTimestamps(handlers.handleMissionTimestampsNotification);
      gcsConnection.listenToMissionRpicInControl(handlers.handleMissionRpicInControlNotification);
      gcsConnection.listenToMissionLogMessage(handlers.handleMissionLogMessageNotification);
      // Subscription is on per mission basis
    };

    const onCenterOpened = (gcsId: string, gcsConnection: GcsConnection) => {
      getLogger(gcsId).info('Center opened, refreshing static data');
      fetchGcsStaticData(gcsId, gcsConnection);
    };

    const onVehiclesUpdated = (gcsId: string, vehicles: readonly Vehicle[]) => {
      getLogger(gcsId).info(`Vehicles updated, subscribing to ${vehicles.length} vehicle(s)`);
      const gcsConnection = gcsConnectionManager.getConnection(gcsId);
      if (!gcsConnection) return;

      // Subscribe to mission computer status for each vehicle
      vehicles.forEach((vehicle) => {
        gcsConnection.subscribeToVehicleMissionComputerStatus(vehicle.id).catch((err) => {
          getLogger(gcsId).error(`Failed to subscribe to mission computer status for vehicle ${vehicle.id}:`, err);
        });
      });
    };

    const fetchCurrentUser = async (gcsId: string, gcsConnection: GcsConnection) => {
      updateGcsData(gcsId, (data) => data.withUser(AsyncAction.loading()));
      try {
        const user = await gcsConnection.fetchCurrentUser();
        updateGcsData(gcsId, (data) => data.withUser(AsyncAction.success(user)));
      } catch (err) {
        getLogger(gcsId).error('Failed to fetch current user:', err);
        updateGcsData(gcsId, (data) => data.withUser(AsyncAction.failure(err)));
      }
    };

    const fetchVehicles = async (gcsId: string, gcsConnection: GcsConnection) => {
      updateGcsData(gcsId, (data) => data.withVehicles(AsyncAction.loading()));
      try {
        const vehicles = await gcsConnection.fetchVehicles();
        updateGcsData(gcsId, (data) => data.withVehicles(AsyncAction.success(vehicles)));
        onVehiclesUpdated(gcsId, vehicles);
      } catch (err) {
        getLogger(gcsId).error('Failed to fetch vehicles:', err);
        updateGcsData(gcsId, (data) => data.withVehicles(AsyncAction.failure(err)));
      }
    };

    const fetchLaunchpads = async (gcsId: string, gcsConnection: GcsConnection) => {
      updateGcsData(gcsId, (data) => data.withLaunchpads(AsyncAction.loading()));
      try {
        const launchpads = await gcsConnection.fetchLaunchpads();
        updateGcsData(gcsId, (data) => data.withLaunchpads(AsyncAction.success(launchpads)));
      } catch (err) {
        getLogger(gcsId).error('Failed to fetch launchpads:', err);
        updateGcsData(gcsId, (data) => data.withLaunchpads(AsyncAction.failure(err)));
      }
    };

    const fetchFlightNetwork = async (gcsId: string, gcsConnection: GcsConnection) => {
      updateGcsData(gcsId, (data) => data.withFlightNetwork(AsyncAction.loading()));
      try {
        const flightNetwork = await gcsConnection.fetchFlightNetwork();
        updateGcsData(gcsId, (data) => data.withFlightNetwork(AsyncAction.success(flightNetwork)));
      } catch (err) {
        getLogger(gcsId).error('Failed to fetch flight network:', err);
        updateGcsData(gcsId, (data) => data.withFlightNetwork(AsyncAction.failure(err)));
      }
    };

    const fetchCenter = async (gcsId: string, gcsConnection: GcsConnection) => {
      updateGcsData(gcsId, (data) => data.withCenter(AsyncAction.loading()));
      try {
        const center = await gcsConnection.fetchCenter();
        updateGcsData(gcsId, (data) => data.withCenter(AsyncAction.success(center)));
      } catch (err) {
        updateGcsData(gcsId, (data) => data.withCenter(AsyncAction.failure(err)));
      }
    };

    return {
      data: new Map(),

      addGcsConnection: (config: GcsConfig) => {
        const gcsId = config.id;
        getLogger(gcsId).info('Adding new GCS connection');

        // Skip if connection already exists
        if (gcsConnectionManager.getConnection(gcsId)) {
          getLogger(gcsId).warn('Connection already exists, skipping');
          return;
        }

        // Create new connection
        const gcsConnection = new GcsConnection(config);
        gcsConnectionManager.addConnection(gcsId, gcsConnection);

        // Initialize data map
        updateGcsData(gcsId, (data) => data);

        // Create handlers and subscribe to state changes
        const handlers = createHandlers(gcsId, gcsConnection);
        const unlisten = gcsConnection.listenToConnectionStateChanged(handlers.handleConnectionStateChanged);
        gcsConnectionStateUnlistenersMap.set(gcsId, unlisten);

        getLogger(gcsId).info('GCS connection added');
      },

      removeGcsConnection: (gcsId: string) => {
        getLogger(gcsId).warn('Removing GCS connection');

        lastFetchGcsStaticDataTimestampMap.delete(gcsId);

        // Clean up foreign aircraft timeouts
        const detailsTimeouts = foreignAircraftDetailsTimeouts.get(gcsId);
        if (detailsTimeouts) {
          detailsTimeouts.forEach((timeout) => clearTimeout(timeout));
          foreignAircraftDetailsTimeouts.delete(gcsId);
        }

        const riskVolumeTimeouts = foreignAircraftRiskVolumeTimeouts.get(gcsId);
        if (riskVolumeTimeouts) {
          riskVolumeTimeouts.forEach((timeout) => clearTimeout(timeout));
          foreignAircraftRiskVolumeTimeouts.delete(gcsId);
        }

        // Remove from connection manager
        gcsConnectionManager.removeConnection(gcsId);

        // Remove from store data
        set((state) => {
          const newData = new Map(state.data);
          newData.delete(gcsId);
          return { data: newData };
        });

        // Clean up listeners
        gcsConnectionStateUnlistenersMap.delete(gcsId);

        // Clean up logger
        loggersMap.delete(gcsId);
      },

      connect: (gcsId: string, queryParams?: Record<string, string>) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);
        getLogger(gcsId).info('Connecting to GCS');
        gcsConnection.connect(queryParams);
      },

      connectAll: () => {
        Array.from(get().data.keys()).forEach((gcsId) => {
          get().connect(gcsId);
        });
      },

      disconnect: (gcsId: string) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);
        getLogger(gcsId).info('Disconnecting from GCS');
        gcsConnection.disconnect();
      },

      disconnectAll: () => {
        Array.from(get().data.keys()).forEach((gcsId) => {
          get().disconnect(gcsId);
        });
      },

      isLoggedIn: async (gcsId: string) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        return await gcsConnection.isLoggedIn();
      },

      signInWithOkta: async (gcsId: string, code: string, redirectUri: string | undefined) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          await gcsConnection.signInWithOkta(code, redirectUri);
        } catch (error) {
          getLogger(gcsId).error(`Failed to sign in with Okta:`, error);
          throw error;
        }
      },

      signOut: async (gcsId: string) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          await gcsConnection.signOut();
        } catch (error) {
          // Treat 401 (Unauthorized) or 403 (Forbidden) as successful sign out
          if (
            error instanceof AppError &&
            (error.code === ErrorCode.HTTP_UNAUTHORIZED || error.code === ErrorCode.HTTP_FORBIDDEN)
          ) {
            getLogger(gcsId).info(`Sign out completed (received ${error.code}), treating as successful sign out`);
            return;
          }
          getLogger(gcsId).error(`Failed to sign out:`, error);
          throw error;
        }
      },

      // Vehicle/Mission Actions
      approveTakeoff: async (gcsId: string, missionId: MissionId) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          await gcsConnection.approveTakeoff(missionId);
        } catch (error) {
          getLogger(gcsId).error(`Failed to approve takeoff for mission ${missionId}:`, error);
          throw error;
        }
      },

      cancelPreflight: async (gcsId: string, missionId: MissionId, requeuePackage: boolean) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          await gcsConnection.cancelPreflight(missionId, requeuePackage);
        } catch (error) {
          getLogger(gcsId).error(`Failed to cancel preflight for mission ${missionId}:`, error);
          throw error;
        }
      },

      safeUp: async (gcsId: string, missionId: MissionId) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          await gcsConnection.safeUp(missionId);
        } catch (error) {
          getLogger(gcsId).error(`Failed to safe up for mission ${missionId}:`, error);
          throw error;
        }
      },

      rtl: async (gcsId: string, missionId: MissionId) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          await gcsConnection.rtl(missionId);
        } catch (error) {
          getLogger(gcsId).error(`Failed to RTL for mission ${missionId}:`, error);
          throw error;
        }
      },

      emergencyLand: async (gcsId: string, missionId: MissionId) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          await gcsConnection.emergencyLand(missionId);
        } catch (error) {
          getLogger(gcsId).error(`Failed to emergency land for mission ${missionId}:`, error);
          throw error;
        }
      },

      triggerParachute: async (gcsId: string, missionId: MissionId) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          await gcsConnection.triggerParachute(missionId);
        } catch (error) {
          getLogger(gcsId).error(`Failed to trigger parachute for mission ${missionId}:`, error);
          throw error;
        }
      },

      triggerFcLoss: async (gcsId: string, missionId: MissionId) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          await gcsConnection.triggerFcLoss(missionId);
        } catch (error) {
          getLogger(gcsId).error(`Failed to trigger FC loss for mission ${missionId}:`, error);
          throw error;
        }
      },

      triggerDaa: async (gcsId: string, missionId: MissionId) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          await gcsConnection.triggerDaa(missionId);
        } catch (error) {
          getLogger(gcsId).error(`Failed to trigger DAA for mission ${missionId}:`, error);
          throw error;
        }
      },

      triggerMotorFailure: async (gcsId: string, vehicleId: VehicleId) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          await gcsConnection.triggerMotorFailure(vehicleId);
        } catch (error) {
          getLogger(gcsId).error(`Failed to trigger motor failure for vehicle ${vehicleId}:`, error);
          throw error;
        }
      },

      triggerVeryLowBattery: async (gcsId: string, vehicleId: VehicleId) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          await gcsConnection.triggerVeryLowBattery(vehicleId);
        } catch (error) {
          getLogger(gcsId).error(`Failed to trigger very low battery for vehicle ${vehicleId}:`, error);
          throw error;
        }
      },

      triggerVibrationExceedance: async (gcsId: string, missionId: MissionId) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          await gcsConnection.triggerVibrationExceedance(missionId);
        } catch (error) {
          getLogger(gcsId).error(`Failed to trigger vibration exceedance for mission ${missionId}:`, error);
          throw error;
        }
      },

      triggerVeering: async (gcsId: string, missionId: MissionId) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          await gcsConnection.triggerVeering(missionId);
        } catch (error) {
          getLogger(gcsId).error(`Failed to trigger veering for mission ${missionId}:`, error);
          throw error;
        }
      },

      triggerGpsDegradation: async (gcsId: string, vehicleId: VehicleId) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          await gcsConnection.triggerGpsDegradation(vehicleId);
        } catch (error) {
          getLogger(gcsId).error(`Failed to trigger GPS degradation for vehicle ${vehicleId}:`, error);
          throw error;
        }
      },

      submitDiscrepancyReport: async (gcsId: string, vehicleId: VehicleId, description: string) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          await gcsConnection.submitDiscrepancyReport(vehicleId, description);
        } catch (error) {
          getLogger(gcsId).error(`Failed to submit discrepancy report for vehicle ${vehicleId}:`, error);
          throw error;
        }
      },

      fetchMissionNote: async (gcsId: string, missionId: MissionId) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          return await gcsConnection.fetchMissionNote(missionId);
        } catch (error) {
          getLogger(gcsId).error(`Failed to fetch mission note for mission ${missionId}:`, error);
          throw error;
        }
      },

      setMissionNote: async (gcsId: string, missionId: MissionId, note: string) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          await gcsConnection.setMissionNote(missionId, note);
        } catch (error) {
          getLogger(gcsId).error(`Failed to set mission note for mission ${missionId}:`, error);
          throw error;
        }
      },

      setCenterOpenState: async (gcsId: string, newState: CenterOpenState) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);
        try {
          const request: SetCenterOpenStateRequest = {
            openState: newState,
            checklists: {
              center: null,
              launchpads: []
            }
          };
          await gcsConnection.setCenterOpenState(request);
        } catch (error) {
          getLogger(gcsId).error(`Failed to set center open state to ${newState}:`, error);
          throw error;
        }
      },

      fetchPostflightMissionSummary: async (gcsId: string, missionId: MissionId): Promise<PostflightMissionSummary> => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        return await gcsConnection.getPostflightMissionSummary(missionId);
      },

      fetchVersion: async (gcsId: string) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          return await gcsConnection.fetchVersion();
        } catch (error) {
          getLogger(gcsId).error(`Failed to fetch version:`, error);
          throw error;
        }
      },

      fetchVehicleAllModuleVersions: async (gcsId: string, vehicleId: VehicleId) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          return await gcsConnection.fetchVehicleAllModuleVersions(vehicleId);
        } catch (error) {
          getLogger(gcsId).error(`Failed to fetch module versions for vehicle ${vehicleId}:`, error);
          throw error;
        }
      },

      restartVehicle: async (gcsId: string, vehicleId: VehicleId) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          await gcsConnection.restartVehicle(vehicleId);
        } catch (error) {
          getLogger(gcsId).error(`Failed to restart vehicle ${vehicleId}:`, error);
          throw error;
        }
      },

      disconnectVehicle: async (gcsId: string, vehicleId: VehicleId) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          await gcsConnection.disconnectVehicle(vehicleId);
        } catch (error) {
          getLogger(gcsId).error(`Failed to disconnect vehicle ${vehicleId}:`, error);
          throw error;
        }
      },

      triggerRemoteIdFailure: async (gcsId: string, vehicleId: VehicleId) => {
        const gcsConnection = getRequiredGcsConnection(gcsId);

        try {
          await gcsConnection.triggerRemoteIdFailure(vehicleId);
        } catch (error) {
          getLogger(gcsId).error(`Failed to trigger remote ID failure for vehicle ${vehicleId}:`, error);
          throw error;
        }
      }
    };
  })
);
